import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { TypedMemo } from '../../common/types';
import { SMLButton } from '../../common/views/SMLButton/SMLButton';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole screen; each part uses its own slice of it */
import styles from './Catalog.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface OwnershipMatrixModalProps {
  isOpen: boolean;
  closeModal: () => void;
}

/**
 * What a claim buys and what it does not.
 *
 * Written as a table rather than a nag: it names exactly what is missing while
 * a release is unconfirmed. The pre-save row says "fan-side only" because that
 * is the truth — the fan authorises their own library, so it works — and the
 * analytics rows are dashes because we cannot read those without the account.
 */
const OwnershipMatrixModalComponent: React.FC<OwnershipMatrixModalProps> = ({ isOpen, closeModal }) => {
  const intl = useIntl();

  const yes = intl.formatMessage({ id: 'app.sml.catalog.matrix.yes', defaultMessage: 'Yes' });
  const no = intl.formatMessage({ id: 'app.sml.catalog.matrix.no', defaultMessage: '—' });

  const rows = useMemo(
    () => [
      {
        label: intl.formatMessage({
          id: 'app.sml.catalog.matrix.row.build',
          defaultMessage: 'Build smartlinks and pre-save pages',
        }),
        claimed: yes,
        verified: yes,
        claimedIsYes: true,
      },
      {
        label: intl.formatMessage({
          id: 'app.sml.catalog.matrix.row.campaigns',
          defaultMessage: 'Schedule posts and run campaigns',
        }),
        claimed: yes,
        verified: yes,
        claimedIsYes: true,
      },
      {
        label: intl.formatMessage({
          id: 'app.sml.catalog.matrix.row.click_stats',
          defaultMessage: 'Click and page analytics',
        }),
        claimed: yes,
        verified: yes,
        claimedIsYes: true,
      },
      {
        label: intl.formatMessage({
          id: 'app.sml.catalog.matrix.row.streams',
          defaultMessage: 'Streams, listeners and saves per market',
        }),
        claimed: no,
        verified: yes,
        claimedIsYes: false,
      },
      {
        label: intl.formatMessage({
          id: 'app.sml.catalog.matrix.row.playlists',
          defaultMessage: 'Playlist adds and editorial signals',
        }),
        claimed: no,
        verified: yes,
        claimedIsYes: false,
      },
      {
        label: intl.formatMessage({
          id: 'app.sml.catalog.matrix.row.presave',
          defaultMessage: 'Pre-save delivered into fans’ libraries',
        }),
        claimed: intl.formatMessage({
          id: 'app.sml.catalog.matrix.row.presave_claimed',
          defaultMessage: 'Fan-side only',
        }),
        verified: yes,
        claimedIsYes: false,
      },
      {
        label: intl.formatMessage({
          id: 'app.sml.catalog.matrix.row.badge',
          defaultMessage: 'Shown as confirmed on your pages',
        }),
        claimed: no,
        verified: yes,
        claimedIsYes: false,
      },
    ],
    [intl, no, yes]
  );

  return (
    <Modal isOpen={isOpen} toggle={closeModal} centered size="lg" className="sml-modal">
      <ModalHeader toggle={closeModal}>
        {intl.formatMessage({ id: 'app.sml.catalog.matrix.title', defaultMessage: 'Claimed and verified' })}
      </ModalHeader>
      <ModalBody>
        <div className={styles.matrixScroll}>
          <table className={styles.matrix}>
            <thead>
              <tr>
                <th aria-label={intl.formatMessage({ id: 'app.sml.catalog.matrix.what', defaultMessage: 'What' })} />
                <th>{intl.formatMessage({ id: 'app.sml.catalog.matrix.claimed', defaultMessage: 'Claimed' })}</th>
                <th>{intl.formatMessage({ id: 'app.sml.catalog.matrix.verified', defaultMessage: 'Verified' })}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.label}>
                  <th scope="row">{row.label}</th>
                  <td className={row.claimedIsYes ? styles.matrixYes : styles.matrixNo}>{row.claimed}</td>
                  <td className={styles.matrixYes}>{row.verified}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className={styles.matrixNote}>
          {intl.formatMessage({
            id: 'app.sml.catalog.matrix.note',
            defaultMessage:
              'A claimed release loses the numbers we cannot see without the account — not the ability to promote it. Verification needs a connected service, which is not built yet; nothing you build now has to be redone when it is.',
          })}
        </p>
      </ModalBody>
      <ModalFooter>
        <SMLButton outline onClick={closeModal}>
          {intl.formatMessage({ id: 'app.buttons.close', defaultMessage: 'Close' })}
        </SMLButton>
      </ModalFooter>
    </Modal>
  );
};

export const OwnershipMatrixModal = TypedMemo(OwnershipMatrixModalComponent);
